import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MessageCircle, Video, Wrench, ShieldCheck, ChevronRight, Loader2, CheckCircle2 } from 'lucide-react';
import { submitSupportRequest } from '../../services/api';

const Support = () => {
    const { token, clientData } = useOutletContext();

    const [ticketMessage, setTicketMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const handleTicketSubmit = async (e) => {
        e.preventDefault();
        if (!ticketMessage.trim()) return;

        setSubmitting(true);
        try {
            await submitSupportRequest(token, clientData, ticketMessage);
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                setShowForm(false);
                setTicketMessage('');
            }, 3000);
        } catch (err) {
            console.error("Support request error:", err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="animate-fade-in max-w-3xl mx-auto pb-24">
            <header className="mb-12 border-b border-[#F0EFF4]/10 pb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Command Center</h1>
                    <p className="text-[#F0EFF4]/60">Your OpenClaw agent is active and listening.</p>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] font-data tracking-widest text-xs rounded-full self-start sm:self-auto">
                    <ShieldCheck className="w-4 h-4" /> SYSTEM OPERATIONAL
                </div>
            </header>

            <div className="grid sm:grid-cols-2 gap-6 mb-16">
                {/* Action Cards */}
                <a href="#" className="group p-6 bg-[#18181B] border border-[#F0EFF4]/10 hover:border-[#7B61FF]/50 rounded-2xl transition-all hover:-translate-y-1 block">
                    <div className="w-12 h-12 bg-[#7B61FF]/10 text-[#7B61FF] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <MessageCircle className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#7B61FF] transition-colors">Message Your Agent</h3>
                    <p className="text-[#F0EFF4]/60 text-sm mb-6">Open your chosen secure messenger to assign tasking or request briefings.</p>
                    <div className="flex items-center text-sm font-bold text-[#7B61FF]">Open Channel <ChevronRight className="w-4 h-4 ml-1" /></div>
                </a>

                <a href="/winning-wednesday" target="_blank" rel="noopener noreferrer" className="group p-6 bg-[#18181B] border border-[#F0EFF4]/10 hover:border-[#10B981]/50 rounded-2xl transition-all hover:-translate-y-1 block">
                    <div className="w-12 h-12 bg-[#10B981]/10 text-[#10B981] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Video className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#10B981] transition-colors">Winning Wednesday</h3>
                    <p className="text-[#F0EFF4]/60 text-sm mb-6">Join the weekly masterclass on maximizing AI utility in your roofing company.</p>
                    <div className="flex items-center text-sm font-bold text-[#10B981]">Register / Join <ChevronRight className="w-4 h-4 ml-1" /></div>
                </a>
            </div>

            {/* Hardware / Esco */}
            <div className="bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                    <Wrench className="w-48 h-48" />
                </div>
                <div className="relative z-10">
                    <h2 className="text-2xl font-bold mb-4">Hardware or Escalation Support</h2>
                    <p className="text-[#F0EFF4]/70 max-w-lg mb-8">
                        If your Mac Mini experiences a hardware failure, connectivity drop, or you need to schedule a direct strategy alignment call with Adam, submit a secure ticket.
                    </p>

                    {!showForm ? (
                        <div className="flex flex-wrap gap-4">
                            <button
                                onClick={() => setShowForm(true)}
                                className="btn-magnetic px-6 py-3 bg-[#F0EFF4] text-[#0A0A14] font-bold rounded-xl hover:bg-[#7B61FF] hover:text-[#F0EFF4] transition-colors">
                                Submit Support Ticket
                            </button>
                            <a href="mailto:support@roofingbusinesspartner.com" className="px-6 py-3 border border-[#F0EFF4]/20 text-[#F0EFF4] font-bold rounded-xl hover:bg-[#18181B] transition-colors">
                                Email Support Direct
                            </a>
                        </div>
                    ) : success ? (
                        <div className="flex items-center gap-3 p-4 bg-[#10B981]/10 text-[#10B981] font-bold rounded-xl border border-[#10B981]/30 max-w-lg">
                            <CheckCircle2 className="w-5 h-5" /> Ticket Submitted to Operations
                        </div>
                    ) : (
                        <form onSubmit={handleTicketSubmit} className="max-w-lg animate-fade-in">
                            <textarea
                                required
                                rows="4"
                                placeholder="Describe the hardware issue or escalation request..."
                                value={ticketMessage}
                                onChange={(e) => setTicketMessage(e.target.value)}
                                className="w-full bg-[#18181B] border border-[#F0EFF4]/20 rounded-xl px-4 py-3 text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none transition-colors resize-none mb-4"
                            />
                            <div className="flex items-center gap-4">
                                <button disabled={submitting} type="submit" className="px-6 py-3 bg-[#7B61FF] hover:bg-[#6b52e8] text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 min-w-[120px]">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
                                </button>
                                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-3 text-[#F0EFF4]/60 hover:text-[#F0EFF4] font-bold text-sm transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Support;
